import React, { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Check } from 'lucide-react';
import { mockData } from '../../mock';
import { Reveal, SectionHeader } from './Shared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const submit = async (path, data, okMsg) => {
  const res = await fetch(`${BACKEND_URL}/api/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('failed');
  toast.success(okMsg, { description: 'Confirmation sent to your email and info@spacehash.club. We reply within 24 hours.' });
};

const failToast = () => toast.error('Something went wrong', { description: 'Please try again or email info@spacehash.club.' });

export const NlBranding = () => {
  const [busy, setBusy] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    setBusy(true);
    try {
      await submit('branding-enquiry', { name: fd.get('name'), email: fd.get('email'), phone: fd.get('phone') }, 'Branding enquiry submitted');
      e.target.reset();
    } catch { failToast(); } finally { setBusy(false); }
  };

  return (
    <section id="branding" data-testid="nl-branding" className="nl-section pt-0">
      <div className="nl-container">
        <Reveal className="relative overflow-hidden rounded-[1.5rem] border border-amber-500/30 bg-amber-500 text-[#0B0D11] p-8 md:p-12 grid lg:grid-cols-12 gap-8 items-center">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-white/20 blur-3xl" />
          <div className="lg:col-span-5 relative">
            <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase">Branding opportunity</span>
            <h3 className="text-3xl md:text-4xl font-black uppercase leading-none mt-3">Put your brand<br />courtside.</h3>
            <p className="mt-4 text-[#0B0D11]/80 text-sm md:text-base max-w-md">High footfall, affluent and active audiences. Court wraps, glass branding, LED and tournament sponsorships available.</p>
          </div>
          <form onSubmit={onSubmit} data-testid="nl-branding-form" className="lg:col-span-7 relative grid sm:grid-cols-4 gap-3">
            <input name="name" required placeholder="Company name" data-testid="nl-branding-name" className="nl-input !bg-[#0B0D11] !border-transparent" />
            <input name="email" type="email" required placeholder="Email" data-testid="nl-branding-email" className="nl-input !bg-[#0B0D11] !border-transparent" />
            <input name="phone" type="tel" required placeholder="Phone" data-testid="nl-branding-phone" className="nl-input !bg-[#0B0D11] !border-transparent" />
            <button type="submit" disabled={busy} data-testid="nl-branding-submit" className="nl-btn nl-btn-dark !bg-[#0B0D11] !text-white hover:!bg-black">
              {busy ? 'Sending…' : 'Enquire'} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const STATS = [
  ['10+', 'Locations planned'],
  ['₹50L+', 'Monthly revenue potential'],
  ['18', 'Months ROI period'],
  ['100%', 'Support & training'],
];

export const NlFranchise = () => {
  const [busy, setBusy] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    setBusy(true);
    try {
      await submit('franchise-enquiry', {
        name: fd.get('name'), email: fd.get('email'), phone: fd.get('phone'), city: fd.get('city'), message: fd.get('message'),
      }, 'Franchise enquiry submitted');
      e.target.reset();
    } catch { failToast(); } finally { setBusy(false); }
  };

  return (
    <section id="franchise" data-testid="nl-franchise" className="nl-section relative">
      <div className="nl-glow w-[32rem] h-[32rem] bg-orange-600/10 -left-40 bottom-0" />
      <div className="nl-container">
        <SectionHeader
          label="Franchise"
          title={<>Join the padel<br />revolution.</>}
          lead="Partner with India's fastest-growing premium padel network. Proven model, full operational support, and a community that shows up."
        />

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
          {STATS.map(([v, l], i) => (
            <Reveal key={l} delay={i} className="bg-[#0B0D11] p-6 md:p-8">
              <div className="nl-display text-3xl md:text-5xl font-black text-amber-500 leading-none">{v}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400 font-bold mt-3">{l}</div>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <Reveal>
              <h3 className="text-2xl font-extrabold">Why a space# franchise?</h3>
              <p className="text-slate-400 mt-3 leading-relaxed">
                Be part of the fastest-growing sport in India. Space# is revolutionizing padel and pickle sports with world-class facilities, cutting-edge technology, and a thriving community. Our proven business model and comprehensive support system ensure your success from day one.
              </p>
            </Reveal>
            <Reveal delay={1} className="mt-8 grid sm:grid-cols-2 gap-3">
              {mockData.franchise.benefits.map((b) => (
                <div key={b} className="flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.03] p-4">
                  <span className="w-6 h-6 rounded-full bg-amber-500 text-[#0B0D11] flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5" /></span>
                  <span className="text-sm text-slate-200">{b}</span>
                </div>
              ))}
            </Reveal>
          </div>

          <Reveal delay={2} className="lg:col-span-6 nl-card p-7 md:p-9">
            <h3 className="text-2xl font-extrabold">Start your journey</h3>
            <p className="text-slate-400 text-sm mt-2">Our partnership team will contact you within 24 hours.</p>
            <form onSubmit={onSubmit} data-testid="nl-franchise-form" className="mt-6 grid sm:grid-cols-2 gap-4">
              <input name="name" required placeholder="Full name *" data-testid="nl-franchise-name" className="nl-input" />
              <input name="email" type="email" required placeholder="Email address *" data-testid="nl-franchise-email" className="nl-input" />
              <input name="phone" type="tel" required placeholder="Phone number *" data-testid="nl-franchise-phone" className="nl-input" />
              <input name="city" required placeholder="Preferred city *" data-testid="nl-franchise-city" className="nl-input" />
              <textarea name="message" rows={4} placeholder="Tell us about your background and why you're interested…" data-testid="nl-franchise-message" className="nl-input sm:col-span-2 resize-none" />
              <button type="submit" disabled={busy} data-testid="nl-franchise-submit" className="nl-btn nl-btn-primary sm:col-span-2">
                {busy ? 'Sending…' : 'Submit partnership enquiry'} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="sm:col-span-2 text-xs text-slate-500">Your information is sent to info@spacehash.club and kept confidential.</p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
